/**
 * The four backdrop states the sky can be in. The animation cross-fades between
 * them as the scrubbed hour / selected day changes.
 */
export type SkyState = 'sunny' | 'cloudy' | 'rain' | 'storm';

/**
 * Daily outlook `weatherIcon` keys (and raw WillyWeather precis codes) or a WWO
 * Marine numeric `weatherCode`. Both providers normalize outlook icons to the
 * shared `weatherPrecis*` set; WWO hourly codes stay numeric for the harness.
 */
export type WeatherConditionInput = number | string | null | undefined;

/** Thunder codes in the WorldWeatherOnline Marine weather-icons table. */
const STORM_CODES = new Set([200, 386, 389, 392, 395]);

/** Cloud and fog codes: overcast but not raining. */
const CLOUDY_CODES = new Set([116, 119, 122, 143, 248, 260]);

/** Everything at or above this code that is not thunder or fog is precipitation. */
const PRECIPITATION_FLOOR = 176;

/**
 * WWO code 113, "Clear/Sunny". Callers that have no weather code use this so the
 * sky gradient is left untouched.
 */
export const CLEAR_SKY_WEATHER_CODE = 113;

/**
 * Standardized Deckee icon keys (`weatherPrecis*`) and WillyWeather precis
 * codes that map to storm / cloudy / rain. Anything else (including clear /
 * fine / unknown) falls through to sunny.
 */
const STORM_ICONS = new Set([
  'weatherPrecisChanceThunderstormFine',
  'weatherPrecisChanceThunderstormCloud',
  'weatherPrecisChanceThunderstormShowers',
  'weatherPrecisThunderstorm',
  'chance-thunderstorm-fine',
  'chance-thunderstorm-cloud',
  'chance-thunderstorm-showers',
  'thunderstorm',
]);

const CLOUDY_ICONS = new Set([
  'weatherPrecisHighCloud',
  'weatherPrecisPartlyCloudy',
  'weatherPrecisMostlyCloudy',
  'weatherPrecisCloudy',
  'weatherPrecisOvercast',
  'weatherPrecisFog',
  'weatherPrecisMist',
  'weatherPrecisDust',
  'high-cloud',
  'partly-cloudy',
  'mostly-cloudy',
  'cloudy',
  'overcast',
  'fog',
  'mist',
  'dust',
]);

const RAIN_ICONS = new Set([
  'weatherPrecisChanceShowerFine',
  'weatherPrecisChanceShowerCloud',
  'weatherPrecisShowerOrTwo',
  'weatherPrecisDrizzle',
  'weatherPrecisFewShowers',
  'weatherPrecisShowersRain',
  'weatherPrecisHeavyShowersRain',
  'weatherPrecisHail',
  'weatherPrecisChanceSnowFine',
  'weatherPrecisChanceSnowCloud',
  'weatherPrecisSnowAndRain',
  'weatherPrecisLightSnow',
  'weatherPrecisSnow',
  'weatherPrecisHeavySnow',
  'chance-shower-fine',
  'chance-shower-cloud',
  'shower-or-two',
  'drizzle',
  'few-showers',
  'showers-rain',
  'heavy-showers-rain',
  'hail',
  'chance-snow-fine',
  'chance-snow-cloud',
  'snow-and-rain',
  'light-snow',
  'snow',
  'heavy-snow',
  // Spec / fixture shorthand used in some client tests
  'rain',
]);

const skyStateForNumericCode = (code: number): SkyState => {
  if (STORM_CODES.has(code)) {
    return 'storm';
  }

  if (CLOUDY_CODES.has(code)) {
    return 'cloudy';
  }

  if (code >= PRECIPITATION_FLOOR) {
    return 'rain';
  }

  return 'sunny';
};

/**
 * Maps a daily weather icon (WillyWeather / World Weather Online outlook) or a
 * WWO Marine numeric `weatherCode` to a backdrop state.
 *
 * Anything unrecognised (including clear / fine) falls through to `sunny`,
 * which leaves the sky gradient untouched.
 */
export const skyStateForWeatherCode = (
  weatherCode: WeatherConditionInput,
): SkyState => {
  if (weatherCode == null || weatherCode === '') {
    return 'sunny';
  }

  if (typeof weatherCode === 'number') {
    return skyStateForNumericCode(weatherCode);
  }

  const trimmed = weatherCode.trim();

  if (/^\d+$/.test(trimmed)) {
    return skyStateForNumericCode(Number(trimmed));
  }

  if (STORM_ICONS.has(trimmed)) {
    return 'storm';
  }

  if (CLOUDY_ICONS.has(trimmed)) {
    return 'cloudy';
  }

  if (RAIN_ICONS.has(trimmed)) {
    return 'rain';
  }

  return 'sunny';
};
